import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';

// Fonts are bundled, not fetched: @fontsource ships the woff2 files through the
// bundler, so a cold start makes zero external requests (spec: "UX & Feel").
import '@fontsource-variable/inter';

import './styles/tokens.css';
import './styles/base.css';
import './styles/shell.css';

import { ApiProvider } from './api/provider';
import { QueryProvider } from './app/query';
import { OfflineCacheProvider } from './offline/OfflineCacheProvider';
import { router } from './app/router';
import { describeError, installErrorReporting, reportClientError } from './errorReporting/reporter';
import { trackLaunchInteraction } from './pwa/updates';
import { captureInstallPrompt } from './pwa/install';

// Before anything renders, so an error thrown while the app starts is heard too
// (ADR 0067). Pop-out windows load this same entry, so they are covered as well.
installErrorReporting();
// The first tap or key ends "just launched": a new version found after that is
// offered with the prompt rather than applied silently (src/pwa/updates.ts).
trackLaunchInteraction();
captureInstallPrompt();

const container = document.getElementById('root');
if (!container) throw new Error('Root container #root is missing from index.html');

/*
 * React 19 routes render errors through these instead of the console. Passing
 * one REPLACES React's default (which, for uncaught errors, is `reportError` --
 * the window 'error' event the beacon already listens to), so each error is
 * reported once, here. React's default console output is kept, in every build:
 * someone with devtools open on a broken phone still sees the error.
 */
const root = createRoot(container, {
  onUncaughtError(error, errorInfo) {
    console.error(error, errorInfo.componentStack);
    reportClientError({ kind: 'error', ...describeError(error) });
  },
  onCaughtError(error, errorInfo) {
    console.error(error, errorInfo.componentStack);
    reportClientError({ kind: 'boundary', ...describeError(error) });
  },
});

root.render(
  <StrictMode>
    {/* Server state first: every route below reads through TanStack Query. */}
    <QueryProvider>
      {/*
        Above the router, and above `SessionProvider` with it: a cold start
        with no connection has to have the persisted session snapshot and the
        last known mail in the cache *before* anything asks the server, or the
        app paints "temporarily unavailable" over mail it is holding (ADR 0059).
      */}
      <OfflineCacheProvider>
        <ApiProvider>
          <RouterProvider router={router} />
        </ApiProvider>
      </OfflineCacheProvider>
    </QueryProvider>
  </StrictMode>,
);
